import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import DayOverview from '../Add/DayOverview'
import BookingDetails from '../Add/BookingDetails'
import useResourceData from '@/hooks/useResourceData'
import useScheduleData from '@/hooks/useScheduleData'
import ScheduleService from '@/services/ScheduleService'
import Loading from '@/widgets/Loading'
import CustomerPicker from '@/widgets/CustomerPicker'
import CustomerCard from '@/widgets/CustomerCard'

const toUTCNoTZ = localStr => {
	if (!localStr) return ''
	const d = new Date(localStr)
	if (Number.isNaN(d.getTime())) return ''
	const pad = n => String(n).padStart(2, '0')
	return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(
		d.getUTCMinutes()
	)}`
}

const toLocalInput = value => {
	if (!value) return ''
	const m = moment(value)
	if (!m.isValid()) return ''
	return m.format('YYYY-MM-DDTHH:mm')
}

const buildEmptyFormState = () => ({
	title: '',
	service: '',
	resourceId: '',
	productId: '',
	customerId: '',
	customerName: '',
	customerTel: '',
	start: '',
	end: '',
	notes: '',
})

const mapBookingToFormState = (booking, resources = []) => {
	if (!booking) return buildEmptyFormState()
	const resourceId = String(booking.resourceId ?? booking.resource?._id ?? booking.resource?.id ?? '')
	const matchedResource = resources.find(r => String(r?._id ?? r?.id ?? '') === resourceId)
	const customerId = booking.customerId?._id ?? booking.customerId?.id ?? booking.customerId ?? ''
	return {
		title: booking.title || booking.service || '',
		service: booking.service || matchedResource?.name || booking.resource?.name || '',
		resourceId,
		productId: String(booking.productId?._id ?? booking.productId?.id ?? booking.productId ?? ''),
		customerId: String(customerId || ''),
		customerName:
			booking.customerName ||
			booking.customerId?.name ||
			booking.customer?.name ||
			`${booking.customer?.firstName || ''} ${booking.customer?.lastName || ''}`.trim(),
		customerTel: booking.customerTel || booking.customer?.tel || booking.customer?.phone || '',
		start: toLocalInput(booking.start),
		end: toLocalInput(booking.end),
		notes: booking.notes || '',
	}
}

function ScheduleEditPage() {
	const { id } = useParams()
	const navigate = useNavigate()
	const location = useLocation()

	const [formState, setFormState] = useState(buildEmptyFormState)
	const [formError, setFormError] = useState('')
	const [hydrated, setHydrated] = useState(false)
	const [booking, setBooking] = useState(null)
	const [bookingLoading, setBookingLoading] = useState(true)
	const [loadError, setLoadError] = useState('')

	const anchorFromState = location.state?.anchorDate || null

	const { data: resourceData, loading: resourcesLoading, error: resourcesError } = useResourceData()
	const safeResources = useMemo(() => (Array.isArray(resourceData) ? resourceData : []), [resourceData])

	const bookingStart = booking?.start || null
	const computedAnchor = useMemo(() => {
		if (anchorFromState) return moment(anchorFromState)
		if (bookingStart) return moment(bookingStart)
		return moment()
	}, [anchorFromState, bookingStart])

	const [dayViewDate, setDayViewDate] = useState(() => computedAnchor.clone())

	useEffect(() => {
		let active = true
		setBooking(null)
		setHydrated(false)
		setFormState(buildEmptyFormState())
		setLoadError('')

		const fetchBooking = async () => {
			setBookingLoading(true)
			try {
				const { data, error } = await ScheduleService.getById(id)
				if (!active) return
				if (error) throw new Error(error)
				if (!data) throw new Error('Booking not found')
				setBooking(data)
			} catch (err) {
				if (!active) return
				setLoadError(err?.message || 'Unable to load booking details.')
			} finally {
				if (active) {
					setBookingLoading(false)
				}
			}
		}

		fetchBooking()
		return () => {
			active = false
		}
	}, [id])

	useEffect(() => {
		if (!booking || hydrated) return
		const mappedState = mapBookingToFormState(booking, safeResources)
		setFormState(mappedState)
		const mappedStart = moment(mappedState.start)
		setDayViewDate(prev => {
			if (mappedStart.isValid()) return mappedStart
			return computedAnchor.clone()
		})
		setHydrated(true)
	}, [booking, hydrated, safeResources, computedAnchor])

	useEffect(() => {
		if (!formState.start) return
		const startMoment = moment(formState.start)
		if (!startMoment.isValid()) return
		setDayViewDate(prev => {
			if (prev?.isSame?.(startMoment, 'day')) return prev
			return startMoment
		})
	}, [formState.start])

	const scheduleRange = useMemo(
		() => ({
			start: dayViewDate.clone().startOf('day').toISOString(),
			end: dayViewDate.clone().endOf('day').toISOString(),
		}),
		[dayViewDate]
	)

	const {
		data: bookingsData,
		loading: bookingsLoading,
		error: bookingsError,
		update: updateBooking,
		updating: updatingBooking,
	} = useScheduleData(scheduleRange.start, scheduleRange.end)
	const safeBookings = useMemo(() => (Array.isArray(bookingsData) ? bookingsData : []), [bookingsData])

	const draftBooking = useMemo(() => {
		if (!formState.resourceId || !formState.start || !formState.end) return null
		return {
			resourceId: formState.resourceId,
			start: formState.start,
			end: formState.end,
		}
	}, [formState.resourceId, formState.start, formState.end])

	const handleCustomerSelect = useCallback(customer => {
		if (!customer) return
		const name =
			customer.name ||
			customer.customerName ||
			`${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
			'Unnamed customer'
		const phone = customer.tel || customer.phone || customer.mobile || ''
		const customerId = String(customer._id ?? customer.customerid ?? customer.id ?? '')
		setFormState(prev => ({ ...prev, customerId, customerName: name, customerTel: phone }))
	}, [])

	const handleCustomerClear = useCallback(() => {
		setFormState(prev => ({ ...prev, customerId: '', customerName: '', customerTel: '' }))
	}, [])

	const handleProductSelect = useCallback(product => {
		if (!product) {
			setFormState(prev => ({ ...prev, productId: '', service: '' }))
			return
		}
		const nextId = product._id ?? product.id ?? ''
		setFormState(prev => ({ ...prev, productId: String(nextId || ''), service: product.name || prev.service }))
	}, [])

	const handleChange = useCallback((field, value) => {
		setFormState(prev => ({ ...prev, [field]: value }))
	}, [])

	const handleServiceChange = useCallback(
		e => {
			const { value } = e.target
			setFormState(prev => {
				const selectedResource = safeResources.find(r => r?.name === value)
				const resourceId = selectedResource
					? String(selectedResource._id ?? selectedResource.id ?? '')
					: prev.resourceId
				return { ...prev, service: value, resourceId }
			})
		},
		[safeResources]
	)

	const handleSlotSelect = useCallback(({ resourceId, resourceName, slotMoment }) => {
		if (!resourceId || !slotMoment) return
		const normalizedStart = slotMoment.clone().seconds(0).milliseconds(0)
		setFormState(prev => ({
			...prev,
			resourceId,
			service: resourceName || prev.service,
			start: normalizedStart.format('YYYY-MM-DDTHH:mm'),
			end: normalizedStart.clone().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
		}))
	}, [])

	const handleDayChange = useCallback(nextValue => {
		const candidate = moment.isMoment(nextValue) ? nextValue.clone() : moment(nextValue)
		if (!candidate.isValid()) return
		setDayViewDate(candidate.clone())
		setFormState(prev => {
			const prevStart = moment(prev.start)
			const prevEnd = moment(prev.end)
			const nextStart = prevStart.isValid()
				? candidate.clone().hour(prevStart.hour()).minute(prevStart.minute())
				: candidate.clone().hour(9).minute(0)
			const durationMinutes = prevStart.isValid() && prevEnd.isValid() ? prevEnd.diff(prevStart, 'minutes') : 60
			const safeDuration = Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes : 60
			const nextEnd = nextStart.clone().add(safeDuration, 'minutes')
			return {
				...prev,
				start: nextStart.format('YYYY-MM-DDTHH:mm'),
				end: nextEnd.format('YYYY-MM-DDTHH:mm'),
			}
		})
	}, [])

	const bookingId = useMemo(() => booking?._id ?? booking?.id ?? id, [booking, id])

	const handleSubmit = useCallback(
		async e => {
			e?.preventDefault()
			setFormError('')

			if (!bookingId) {
				setFormError('Missing booking reference. Please refresh the page.')
				return
			}

			const chosenResourceId = formState.resourceId || String(safeResources[0]?._id ?? safeResources[0]?.id ?? '')

			const payload = {
				title: formState.title?.trim() || formState.service || '',
				resourceId: chosenResourceId,
				productId: formState.productId,
				productName: formState.service?.trim() || undefined,
				customerId: formState.customerId,
				customerName: formState.customerName,
				customerTel: formState.customerTel,
				start: toUTCNoTZ(formState.start),
				end: toUTCNoTZ(formState.end),
				notes: formState.notes?.trim() || undefined,
			}

			try {
				const success = await updateBooking(bookingId, payload)
				if (success) {
					navigate('/schedule', { state: { message: 'Booking updated successfully.' } })
					return
				}
				setFormError('Unable to update the booking. Please try again.')
			} catch (err) {
				setFormError(err?.message || 'Unable to update the booking. Please try again.')
			}
		},
		[bookingId, formState, navigate, safeResources, updateBooking]
	)

	if (bookingLoading || resourcesLoading || bookingsLoading || !hydrated) {
		return <Loading />
	}

	if (loadError) {
		return (
			<div className="bg-white rounded-2xl shadow-sm p-6">
				<div className="space-y-4">
					<h1 className="text-2xl font-semibold text-gray-900">Edit Booking</h1>
					<p className="text-sm text-gray-600">{loadError}</p>
					<div className="flex gap-3">
						<button
							type="button"
							onClick={() => navigate('/schedule')}
							className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
								Back to schedule
							</button>
						<button
							type="button"
							onClick={() => window.location.reload()}
							className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
								Retry
							</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<div className="bg-white rounded-2xl shadow-sm p-6">
				<div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900">Edit Booking</h1>
						<p className="text-sm text-gray-500">Update the booking details and review other items for the day.</p>
					</div>
					<button
						type="button"
						onClick={() => navigate('/schedule')}
						className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
							Back to schedule
						</button>
				</div>

				{resourcesError && (
					<div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">{resourcesError}</div>
				)}
				{bookingsError && (
					<div className="mb-4 rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">{bookingsError}</div>
				)}

				<div className="space-y-6">
					<section className="rounded-2xl border border-gray-200 p-4">
						<div className="mb-4">
							<h3 className="text-base font-semibold text-gray-900">Customer</h3>
							<p className="text-sm text-gray-500">Review or change the customer linked to this booking.</p>
						</div>
						{formState.customerId ? (
							<CustomerCard customerId={formState.customerId} handleChange={handleCustomerClear} />
						) : (
							<CustomerPicker onCustomerSelect={handleCustomerSelect} />
						)}
					</section>


					<DayOverview
						date={dayViewDate}
						resources={safeResources}
						bookings={safeBookings}
						onSlotSelect={handleSlotSelect}
						draftBooking={draftBooking}
						onDateChange={handleDayChange}
					/>

					<BookingDetails
						formState={formState}
						formError={formError}
						onSubmit={handleSubmit}
						onFieldChange={handleChange}
						onServiceChange={handleServiceChange}
						onProductSelect={handleProductSelect}
						onCancel={() => navigate('/schedule')}
						creating={updatingBooking}
						resources={safeResources}
						mode="edit"
					/>
				</div>
			</div>
		</div>
	)
}

export default ScheduleEditPage
